import { ComponentProps, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'
import {
    formatFirstFacilityAddressField,
    formatSecondFacilityAddressField,
} from '../../../utils/formatFacilityAddress'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetMetersRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Meter } from '@/hooks/useApi/types'

export type MeterFilterProps = {
    name: string
    onChange: (meterIds: string[]) => void
    value: string[]
    defaultValue: string[]
    valueLabel?: string
    params: Partial<GetMetersRequestParams>
    className?: string
} & Pick<
    FilterProps,
    'containerRef' | 'disabled' | 'disabledLabel' | 'notAvailable'
>

export const MeterFilter: React.FC<MeterFilterProps> = (props) => {
    const { value, defaultValue, name, notAvailable, params, onChange } = props
    const { useMetersQuery } = useFiltersApi()

    const query = useMetersQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = useMemo<Map<string, Meter>>(() => {
        if (!data) return new Map<string, Meter>()
        return new Map(data.map((meter) => [meter.meterId.toString(), meter]))
    }, [data])

    const options = data?.map((meter) => {
        return {
            value: meter.meterId.toString(),
            label: meter.meterName,
        }
    })

    const renderRichLabel = useCallback(
        (
            option: Option,
            highlightMatchingText?: (
                input: string
            ) => string | React.JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )
                const meterName = `${rawOption.meterNumber} - ${rawOption.meterName}`

                return (
                    <div className="space-y-0.5">
                        <div>
                            {highlightMatchingText?.(meterName) ?? meterName}
                        </div>
                        <div className="text-body-small text-subtle">
                            {highlightMatchingText?.(firstAddressField) ??
                                firstAddressField}
                        </div>
                        {secondAddressField && (
                            <div className="text-body-small text-subtle">
                                {highlightMatchingText?.(secondAddressField) ??
                                    secondAddressField}
                            </div>
                        )}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const useSearchParams = useCallback(
        (searchValue: string) => ({
            options: options,
            compareFn: (option: Option) => {
                if (typeof option.value !== 'string') return false

                const rawOption = optionMap.get(option.value)
                if (!rawOption) {
                    return false
                }
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    rawOption.meterName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    rawOption.meterNumber
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    rawOption.facilityName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    firstAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    secondAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim())
                )
            },
        }),
        [options, optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.meter.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.meter.label`),
        disabled: props.disabled,
        disabledLabel: props.disabledLabel,
        notAvailable,
        useSearchParams,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        ...labels,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
    }

    return <SelectFilter {...passedProps} />
}
